import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SidebarGroup from '../SidebarGroup';

const MOBILE_BREAKPOINT = 960;

interface PortalSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
}

const PortalSidebar: React.FC<PortalSidebarProps> = ({
  collapsed,
  onToggle,
  mobileOpen = false,
  onCloseMobile
}) => {
  const location = useLocation();
  const { isAuthenticated, isCandidate, isRecruiter, isOrgAdmin } = useAuth();

  const isActive = (path: string) => location.pathname === path;
  const isDashboard = location.pathname.includes('dashboard');
  const isCandidateJobs = location.pathname === '/jobs' || location.pathname.startsWith('/jobs/');

  const handleNavClick = () => {
    if (window.innerWidth <= MOBILE_BREAKPOINT) {
      onCloseMobile?.();
    }
  };

  if (!isAuthenticated || (!isCandidate && !isRecruiter)) {
    return null;
  }

  return (
    <aside className={`sidebar portal-v2-sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
      <div className="sidebar__top portal-v2-sidebar__top">
        <Link to="/" className="sidebar__brand portal-v2-sidebar__brand" onClick={handleNavClick}>
          <span className="brand-badge">TJ</span>
          {!collapsed && <span>talixjobs</span>}
        </Link>
        <button className="sidebar__toggle" onClick={onToggle} aria-label="Toggle sidebar">
          <span />
          <span />
          <span />
        </button>
      </div>

      <nav className="sidebar__nav">
        {isCandidate && (
          <>
            <Link
              to="/candidate/dashboard"
              className={`sidebar__link ${isDashboard ? 'active' : ''}`}
              aria-current={isDashboard ? 'page' : undefined}
              onClick={handleNavClick}
            >
              <span className="sidebar__label">Dashboard</span>
            </Link>
            <Link to="/jobs" className={`sidebar__link ${isCandidateJobs ? 'active' : ''}`} aria-current={isCandidateJobs ? 'page' : undefined} onClick={handleNavClick}>
              <span className="sidebar__label">Browse Jobs</span>
            </Link>
            <Link to="/applications" className={`sidebar__link ${isActive('/applications') ? 'active' : ''}`} aria-current={isActive('/applications') ? 'page' : undefined} onClick={handleNavClick}>
              <span className="sidebar__label">My Applications</span>
            </Link>
            <Link to="/saved-jobs" className={`sidebar__link ${isActive('/saved-jobs') ? 'active' : ''}`} aria-current={isActive('/saved-jobs') ? 'page' : undefined} onClick={handleNavClick}>
              <span className="sidebar__label">Saved Jobs</span>
            </Link>
            <Link to="/pricing" className={`sidebar__link ${isActive('/pricing') ? 'active' : ''}`} aria-current={isActive('/pricing') ? 'page' : undefined} onClick={handleNavClick}>
              <span className="sidebar__label">Pricing</span>
            </Link>
            <Link to="/help" className={`sidebar__link ${isActive('/help') ? 'active' : ''}`} aria-current={isActive('/help') ? 'page' : undefined} onClick={handleNavClick}>
              <span className="sidebar__label">Help Center</span>
            </Link>
            <Link to="/profile" className={`sidebar__link ${isActive('/profile') ? 'active' : ''}`} aria-current={isActive('/profile') ? 'page' : undefined} onClick={handleNavClick}>
              <span className="sidebar__label">Profile</span>
            </Link>
          </>
        )}

        {isRecruiter && (
          <>
            <SidebarGroup
              title="Overview"
              storageKey="sidebar-group:recruiter:overview"
              activePaths={['/recruiter/dashboard']}
              hidden={collapsed}
            >
              <Link
                to="/recruiter/dashboard"
                className={`sidebar__link ${isDashboard ? 'active' : ''}`}
                aria-current={isDashboard ? 'page' : undefined}
                onClick={handleNavClick}
              >
                <span className="sidebar__label">Dashboard</span>
              </Link>
            </SidebarGroup>
            <SidebarGroup
              title="Work"
              storageKey="sidebar-group:recruiter:work"
              activePaths={['/recruiter/jobs', '/recruiter/applications', '/recruiter/talent', '/recruiter/buckets', '/recruiter/delegated-jobs']}
              hidden={collapsed}
            >
              <Link to="/recruiter/jobs" className={`sidebar__link ${isActive('/recruiter/jobs') ? 'active' : ''}`} aria-current={isActive('/recruiter/jobs') ? 'page' : undefined} onClick={handleNavClick}>
                <span className="sidebar__label">My Jobs</span>
              </Link>
              <Link to="/recruiter/applications" className={`sidebar__link ${isActive('/recruiter/applications') ? 'active' : ''}`} aria-current={isActive('/recruiter/applications') ? 'page' : undefined} onClick={handleNavClick}>
                <span className="sidebar__label">Applications</span>
              </Link>
              <Link to="/recruiter/talent" className={`sidebar__link ${isActive('/recruiter/talent') ? 'active' : ''}`} aria-current={isActive('/recruiter/talent') ? 'page' : undefined} onClick={handleNavClick}>
                <span className="sidebar__label">Talent Search</span>
              </Link>
              <Link to="/recruiter/buckets" className={`sidebar__link ${isActive('/recruiter/buckets') ? 'active' : ''}`} aria-current={isActive('/recruiter/buckets') ? 'page' : undefined} onClick={handleNavClick}>
                <span className="sidebar__label">Buckets</span>
              </Link>
              <Link to="/recruiter/delegated-jobs" className={`sidebar__link ${location.pathname.startsWith('/recruiter/delegated-jobs') ? 'active' : ''}`} aria-current={location.pathname.startsWith('/recruiter/delegated-jobs') ? 'page' : undefined} onClick={handleNavClick}>
                <span className="sidebar__label">Delegated Jobs</span>
              </Link>
            </SidebarGroup>

            {isOrgAdmin && (
              <SidebarGroup
                title="Org admin"
                storageKey="sidebar-group:recruiter:org-admin"
                activePaths={['/recruiter/org-members', '/recruiter/org/jobs', '/recruiter/org/invites', '/recruiter/org/partners']}
                hidden={collapsed}
              >
                <Link to="/recruiter/org-members" className={`sidebar__link ${isActive('/recruiter/org-members') ? 'active' : ''}`} aria-current={isActive('/recruiter/org-members') ? 'page' : undefined} onClick={handleNavClick}>
                  <span className="sidebar__label">Org Members</span>
                </Link>
                <Link to="/recruiter/org/jobs" className={`sidebar__link ${isActive('/recruiter/org/jobs') ? 'active' : ''}`} aria-current={isActive('/recruiter/org/jobs') ? 'page' : undefined} onClick={handleNavClick}>
                  <span className="sidebar__label">All Org Jobs</span>
                </Link>
                <Link to="/recruiter/org/invites" className={`sidebar__link ${isActive('/recruiter/org/invites') ? 'active' : ''}`} aria-current={isActive('/recruiter/org/invites') ? 'page' : undefined} onClick={handleNavClick}>
                  <span className="sidebar__label">Org Invites</span>
                </Link>
                <Link to="/recruiter/org/partners" className={`sidebar__link ${isActive('/recruiter/org/partners') ? 'active' : ''}`} aria-current={isActive('/recruiter/org/partners') ? 'page' : undefined} onClick={handleNavClick}>
                  <span className="sidebar__label">Approved Partners</span>
                </Link>
              </SidebarGroup>
            )}

            <SidebarGroup
              title="Account"
              storageKey="sidebar-group:recruiter:account"
              activePaths={['/recruiter/profile', '/pricing', '/help']}
              hidden={collapsed}
            >
              <Link to="/pricing" className={`sidebar__link ${isActive('/pricing') ? 'active' : ''}`} aria-current={isActive('/pricing') ? 'page' : undefined} onClick={handleNavClick}>
                <span className="sidebar__label">Pricing</span>
              </Link>
              <Link to="/help" className={`sidebar__link ${isActive('/help') ? 'active' : ''}`} aria-current={isActive('/help') ? 'page' : undefined} onClick={handleNavClick}>
                <span className="sidebar__label">Help Center</span>
              </Link>
              <Link to="/recruiter/profile" className={`sidebar__link ${isActive('/recruiter/profile') ? 'active' : ''}`} aria-current={isActive('/recruiter/profile') ? 'page' : undefined} onClick={handleNavClick}>
                <span className="sidebar__label">Profile</span>
              </Link>
            </SidebarGroup>
          </>
        )}
      </nav>
    </aside>
  );
};

export default PortalSidebar;

